import { Chip, Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import { useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { NextThinkingModeEntity, TimelineWidgetProps } from '../types';

export interface NextThinkingModeWidgetProps extends TimelineWidgetProps<NextThinkingModeEntity> {
  debugMode?: boolean;
}

// Register renderer + visibility
registerWidgetRenderer(
  'next_thinking_mode',
  ({ entity, debugMode }) => (
    <NextThinkingModeWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

export function NextThinkingModeWidget({ entity, debugMode = false }: NextThinkingModeWidgetProps) {
  const { status, mode, reasoning, confidence } = entity.props;
  const [expanded, setExpanded] = useState(true);

  const statusColor = status === 'completed' ? 'success' : status === 'error' ? 'error' : 'primary';
  const confidenceColor =
    confidence === 'high' ? 'success' : confidence === 'medium' ? 'warning' : 'default';

  return (
    <Paper variant="outlined" className="p-4 bg-gradient-to-r from-green-50 to-teal-50">
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main` }}>
          {status === 'active'
            ? 'lightbulb'
            : status === 'completed'
              ? 'check_circle'
              : 'error_outline'}
        </Icon>

        <div className="flex-1">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              💡 Next Thinking Mode
            </Typography>
            {mode ? <Chip label={mode} size="small" color="primary" variant="outlined" /> : null}
            {confidence ? (
              <Chip
                label={`Confidence: ${confidence}`}
                size="small"
                color={confidenceColor}
                variant="outlined"
              />
            ) : null}
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {status === 'active' || status === 'completed' ? (
              <div className="mt-3 space-y-3">
                {reasoning ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-1">
                      Reasoning
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {reasoning}
                    </Typography>
                  </div>
                ) : null}

                {status === 'completed' && entity.props.error ? (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <Typography variant="body2" color="error">
                      Error: {entity.props.error}
                    </Typography>
                  </div>
                ) : null}

                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}

            {status === 'error' ? (
              <div className="mt-3">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <Typography variant="body2" color="error">
                    {entity.props.error || 'An error occurred while suggesting next thinking mode'}
                  </Typography>
                </div>
                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2 mt-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}
